import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type CoupleDashboard } from '../lib/api.js';
import { formatMoney } from '../lib/format.js';
import { Skeleton } from '../components/Skeleton.js';

/**
 * Both partners' own numbers, side by side — mutual, view-only in both
 * directions — plus a combined summary once married. The combined figures
 * are computed here from each partner's own dashboard; nothing about either
 * partner's actual accounts or transactions is ever moved or re-owned. §DEMO
 */
export function CoupleView() {
  const [data, setData] = useState<CoupleDashboard | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.couple.dashboard()
      .then(setData)
      .catch(() => setError('אין לך כרגע בן/בת זוג מחוברים, או שההזמנה עדיין ממתינה.'));
  }, []);

  if (error) {
    return (
      <main className="screen stack">
        <p className="label" style={{ color: 'var(--danger)' }}>{error}</p>
        <Link to="/profile" className="btn">חזרה לפרופיל</Link>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="screen stack">
        <Skeleton height={140} radius={16} />
        <Skeleton height={140} radius={16} />
      </main>
    );
  }

  return (
    <main className="screen stack">
      <div className="stack" style={{ gap: 'var(--sp-1)' }}>
        <h1 style={{ fontSize: 22, fontWeight: 700 }}>אתם ובן/בת הזוג</h1>
        <p className="subtle label--tiny">
          צפייה הדדית בלבד — אף אחד מכם לא יכול לערוך את הנתונים של האחר/ת מכאן.
          {data.status === 'married' && ' נשואים: התמונה למטה מוצגת גם מאוחדת.'}
        </p>
      </div>

      {data.merged && (
        <div className="card stack">
          <span className="card-title">התמונה המשותפת</span>
          <span style={{ fontSize: 28, fontWeight: 700 }}>{formatMoney(data.merged.netWorth)}</span>
          <span className="label--tiny subtle">שווי נטו משותף — סכום שני החשבונות, בלי שום נתון הועבר או אוחד בפועל</span>
          <div className="grid" style={{ gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--sp-3)', marginTop: 8 }}>
            <div className="stack" style={{ gap: 2 }}>
              <span className="label--tiny">פנוי להוצאה (שניכם)</span>
              <span style={{ fontWeight: 600 }}>{formatMoney(data.merged.safeToSpend)}</span>
            </div>
            <div className="stack" style={{ gap: 2 }}>
              <span className="label--tiny">הכנסות החודש</span>
              <span style={{ fontWeight: 600 }}>{formatMoney(data.merged.income)}</span>
            </div>
            <div className="stack" style={{ gap: 2 }}>
              <span className="label--tiny">הוצאות החודש</span>
              <span style={{ fontWeight: 600 }}>{formatMoney(data.merged.expenses)}</span>
            </div>
          </div>
        </div>
      )}

      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-3)' }}>
        <PartnerCard label={data.self.user.name ?? 'המצב שלך'} dashboard={data.self.dashboard} />
        <PartnerCard label={data.partner.user.name ?? 'המצב שלה/ו'} dashboard={data.partner.dashboard} />
      </div>
    </main>
  );
}

function PartnerCard({ label, dashboard }: { label: string; dashboard: CoupleDashboard['self']['dashboard'] }) {
  return (
    <div className="card stack">
      <span className="card-title">{label}</span>
      <span style={{ fontSize: 20, fontWeight: 700 }}>{formatMoney(dashboard.safeToSpend.amount)}</span>
      <span className="label--tiny subtle">פנוי להוצאה</span>
      <div className="stack" style={{ gap: 4, marginTop: 8 }}>
        <Row label="שווי נטו" value={dashboard.position.netWorth} />
        <Row label="הכנסות החודש" value={dashboard.month.income} />
        <Row label="הוצאות החודש" value={dashboard.month.expenses} />
        <Row label="נחסך החודש" value={dashboard.month.saved} />
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: number }) {
  return (
    <div className="between">
      <span className="label--tiny">{label}</span>
      <span style={{ fontWeight: 600, fontSize: 13 }}>{formatMoney(value)}</span>
    </div>
  );
}
